/**
 * Application Store
 * Zustand store for UI state management
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Mention, UnreadMentionCounts } from '@/types/api.types';
import { getStorageAdapter } from '@/services/platform';

export interface TypingUser {
  userId: string;
  displayName: string;
  isBot?: boolean;
}

interface AppState {
  // Active channel/conversation
  activeChannelId: string | null;
  activeDMThreadId: string | null;
  activeThreadMessageId: string | null; // For viewing threads in RightPanel

  // UI state
  sidebarCollapsed: boolean;
  activeView: 'channels' | 'dms' | 'recordings' | 'admin' | 'settings' | 'analytics';
  rightPanelView: 'thread' | 'bookmarks' | 'recordings' | 'members' | null; // Overlay views for right panel
  rightSidebarTab: 'members' | 'media' | 'files' | 'links'; // Active tab in always-on sidebar
  searchOpen: boolean; // Inline search panel visibility

  // Typing indicators
  typingUsers: Record<string, TypingUser[]>; // channelId -> TypingUser[]

  // Mention tracking
  unreadMentions: Record<string, Mention[]>; // conversationId -> Mention[]
  mentionCounts: UnreadMentionCounts;

  // Per-conversation unsent message drafts (conversationId -> text)
  drafts: Record<string, string>;
}

interface AppActions {
  // Channel selection
  setActiveChannel: (channelId: string | null) => void;
  setActiveDMThread: (dmThreadId: string | null) => void;
  setActiveThreadMessage: (messageId: string | null) => void;

  // UI actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setActiveView: (view: 'channels' | 'dms' | 'recordings' | 'admin' | 'settings' | 'analytics') => void;

  // Aliases for GlobalSearchModal compatibility
  setSelectedChannelId: (channelId: string | null) => void;
  setSelectedDMThreadId: (dmThreadId: string | null) => void;
  setRightPanelView: (view: 'thread' | 'bookmarks' | 'recordings' | 'members' | null) => void;
  setRightSidebarTab: (tab: 'members' | 'media' | 'files' | 'links') => void;
  setSearchOpen: (open: boolean) => void;

  // Typing indicators
  addTypingUser: (channelId: string, user: TypingUser) => void;
  removeTypingUser: (channelId: string, userId: string) => void;
  clearTypingUsers: (channelId: string) => void;

  // Mention actions
  setUnreadMentions: (conversationId: string, mentions: Mention[]) => void;
  addMention: (conversationId: string, mention: Mention) => void;
  markMentionRead: (conversationId: string, mentionId: string) => void;
  setMentionCounts: (counts: UnreadMentionCounts) => void;
  updateMentionCount: (conversationId: string, delta: number, type: 'channel' | 'dm') => void;

  // Draft actions
  setDraft: (conversationId: string, text: string) => void;
  clearDraft: (conversationId: string) => void;

  // Reset
  reset: () => void;
}

export type AppStore = AppState & AppActions;

const initialState: AppState = {
  activeChannelId: null,
  activeDMThreadId: null,
  activeThreadMessageId: null,
  sidebarCollapsed: false,
  activeView: 'channels',
  rightPanelView: null,
  rightSidebarTab: 'members',
  searchOpen: false,
  typingUsers: {},
  unreadMentions: {},
  mentionCounts: {
    channels: {},
    dms: {},
    total: 0,
  },
  drafts: {},
};

export const useAppStore = create<AppStore>()(
  persist(
    (set) => ({
      // Initial state
      ...initialState,

      // Actions
      setActiveChannel: (channelId) =>
        set({
          activeChannelId: channelId,
          activeDMThreadId: null, // Clear DM when selecting channel
          activeView: 'channels',
        }),

      setActiveDMThread: (dmThreadId) =>
        set({
          activeDMThreadId: dmThreadId,
          activeChannelId: null, // Clear channel when selecting DM
          activeView: 'dms',
        }),

      setActiveThreadMessage: (messageId) =>
        set({
          activeThreadMessageId: messageId,
        }),

      toggleSidebar: () =>
        set((state) => ({
          sidebarCollapsed: !state.sidebarCollapsed,
        })),

      setSidebarCollapsed: (collapsed) =>
        set({
          sidebarCollapsed: collapsed,
        }),

      setActiveView: (view) =>
        set({
          activeView: view,
        }),

      setRightPanelView: (view) =>
        set({
          rightPanelView: view,
        }),

      setRightSidebarTab: (tab) =>
        set({
          rightSidebarTab: tab,
        }),

      setSearchOpen: (open) =>
        set({
          searchOpen: open,
        }),

      // Aliases for GlobalSearchModal compatibility
      setSelectedChannelId: (channelId) =>
        set({
          activeChannelId: channelId,
          activeDMThreadId: null,
          activeView: 'channels',
        }),

      setSelectedDMThreadId: (dmThreadId) =>
        set({
          activeDMThreadId: dmThreadId,
          activeChannelId: null,
          activeView: 'dms',
        }),

      addTypingUser: (channelId, user) =>
        set((state) => ({
          typingUsers: {
            ...state.typingUsers,
            [channelId]: [
              ...(state.typingUsers[channelId] || []).filter((u) => u.userId !== user.userId),
              user,
            ],
          },
        })),

      removeTypingUser: (channelId, userId) =>
        set((state) => ({
          typingUsers: {
            ...state.typingUsers,
            [channelId]: (state.typingUsers[channelId] || []).filter(
              (u) => u.userId !== userId
            ),
          },
        })),

      clearTypingUsers: (channelId) =>
        set((state) => ({
          typingUsers: {
            ...state.typingUsers,
            [channelId]: [],
          },
        })),

      setUnreadMentions: (conversationId, mentions) =>
        set((state) => ({
          unreadMentions: {
            ...state.unreadMentions,
            [conversationId]: mentions,
          },
        })),

      addMention: (conversationId, mention) =>
        set((state) => ({
          unreadMentions: {
            ...state.unreadMentions,
            [conversationId]: [
              ...(state.unreadMentions[conversationId] || []),
              mention,
            ],
          },
        })),

      markMentionRead: (conversationId, mentionId) =>
        set((state) => ({
          unreadMentions: {
            ...state.unreadMentions,
            [conversationId]: (state.unreadMentions[conversationId] || []).map((m) =>
              m.id === mentionId ? { ...m, is_read: true, read_at: new Date().toISOString() } : m
            ),
          },
        })),

      setMentionCounts: (counts) =>
        set({
          mentionCounts: counts,
        }),

      updateMentionCount: (conversationId, delta, type) =>
        set((state) => {
          const newCounts = { ...state.mentionCounts };
          const targetCounts = type === 'channel' ? newCounts.channels : newCounts.dms;
          targetCounts[conversationId] = Math.max(0, (targetCounts[conversationId] || 0) + delta);
          newCounts.total = Math.max(0, newCounts.total + delta);
          return { mentionCounts: newCounts };
        }),

      setDraft: (conversationId, text) =>
        set((state) => ({
          drafts: {
            ...state.drafts,
            [conversationId]: text,
          },
        })),

      clearDraft: (conversationId) =>
        set((state) => {
          if (!(conversationId in state.drafts)) return {};
          const newDrafts = { ...state.drafts };
          delete newDrafts[conversationId];
          return { drafts: newDrafts };
        }),

      reset: () => set(initialState),
    }),
    {
      name: 'app-storage',
      storage: createJSONStorage(() => getStorageAdapter()),
      partialize: (state) => ({
        activeChannelId: state.activeChannelId,
        activeDMThreadId: state.activeDMThreadId,
        sidebarCollapsed: state.sidebarCollapsed,
        activeView: state.activeView,
        drafts: state.drafts,
      }),
    }
  )
);
